/**
 * One collection (SPEC §22), on the shared design system. Owners rename, reorder (drag or arrows), add notes,
 * remove reels and share; viewers browse, open reels and "Save a copy". Also serves signed-in links /l/<token>.
 */
import { plural } from '@vw/core/src/plural';
import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Copy, FolderHeart, Link2, Pencil, Share2, Trash2, X } from 'lucide-react';
import { Button, ConfirmDialog, EmptyState, Input, Pill, Spinner, TierBadge, cn, copyText, formatWiseScore, toast } from '@vw/ui';
import { api, ApiError, type CollectionView, type ItemReel, type Visibility } from '../api';
import { ReelInspector } from '../components/workbench/ReelInspector';
import { compact, date, duration } from '../format';
import { Link, navigate } from '../router';
import { useSession } from '../session';

export const VIS_LABEL: Record<Visibility, string> = {
  private: 'Private', people: 'Specific people', signed_in_link: 'Signed-in users with the link', anyone_link: 'Anyone with the link', public: 'Public',
};
const VIS_HELP: Record<Visibility, string> = {
  private: 'Only you.',
  people: 'You and the people you invite (they sign in with that Google account).',
  signed_in_link: 'Anyone signed in to the app who has the link.',
  anyone_link: 'Anyone with the link, no sign-in. They see reels, scores and your notes; transcripts need sign-in.',
  public: 'Listed in Explore. Only the team can publish.',
};

export function Collection({ id, token }: { id?: string; token?: string }) {
  const { settings } = useSession();
  const [c, setC] = useState<CollectionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [marks, setMarks] = useState<Set<string> | null>(null);

  const path = token ? `/api/collections/by-token/${token}` : `/api/collections/${id}`;
  const load = () => api<CollectionView>(path).then(setC, (e) => setError(e instanceof ApiError && e.status === 404 ? 'not_found' : e.message));
  useEffect(() => { void load(); }, [path]);
  // Bookmark state for the inspector, loaded the first time a reel is opened.
  useEffect(() => {
    if (open === null || marks) return;
    api<{ reelPks: string[] }>('/api/bookmarks/ids').then((r) => setMarks(new Set(r.reelPks)), () => setMarks(new Set()));
  }, [open, marks]);

  if (error === 'not_found') {
    return <EmptyState icon={<FolderHeart className="h-7 w-7" />} title="Collection not found" actions={<Link to="/collections" className="text-sm font-semibold text-primary">Your collections →</Link>}>It may be private, the link may have been turned off, or it was deleted.</EmptyState>;
  }
  if (error) return <p className="text-sm text-rose-500">{error}</p>;
  if (!c) return <Spinner label="Loading collection…" />;

  const owner = c.role === 'owner';
  const run = async (fn: () => Promise<unknown>) => {
    try {
      const r = await fn();
      if (r && typeof r === 'object' && 'items' in (r as object)) setC(r as CollectionView);
      else await load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const order = c.items.map((i) => i.reelPk);
  const move = (pk: string, to: number) => {
    const from = order.indexOf(pk);
    if (from < 0 || to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    next.splice(to, 0, next.splice(from, 1)[0]!);
    setC({ ...c, items: next.map((p) => c.items.find((i) => i.reelPk === p)!) }); // optimistic
    void run(() => api(`/api/collections/${c.id}/reorder`, { method: 'POST', json: { reelPks: next } }));
  };
  const openable = c.items.filter((i) => i.reel).map((i) => i.reel!);
  const openIdx = openable.findIndex((r) => r.pk === open);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      {c.takedown && <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-600 dark:text-rose-400">This collection was taken down{c.takedown.reason ? `: ${c.takedown.reason}` : ''}. Only you can see it.</div>}
      <section className="flex flex-col gap-5 rounded-3xl border border-border bg-card p-6 shadow-sm sm:flex-row sm:items-start sm:p-8">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-secondary text-4xl">{c.emoji ?? '📁'}</span>
        <div className="min-w-0 flex-1">
          {owner && editing ? (
            <form className="max-w-xl space-y-2" onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void run(() => api(`/api/collections/${c.id}`, { method: 'PATCH', json: { name: f.get('name'), description: f.get('description'), emoji: f.get('emoji') || null } }));
              setEditing(false);
            }}>
              <div className="flex gap-2">
                <input name="emoji" defaultValue={c.emoji ?? ''} aria-label="Emoji" maxLength={8} className="w-16 rounded-xl border border-border bg-background px-2 py-2 text-center outline-none focus:border-primary" />
                <input name="name" defaultValue={c.name} aria-label="Name" maxLength={80} required className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm font-bold outline-none focus:border-primary" />
              </div>
              <textarea name="description" defaultValue={c.description ?? ''} aria-label="Description" maxLength={500} rows={2} placeholder="What's this collection for?"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <div className="flex gap-2"><Button type="submit">Save</Button><Button type="button" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button></div>
            </form>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{c.name}</h1>
              {c.description && <p className="mt-1 max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">{c.description}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{plural(c.items.length, 'reel')}</span><Pill tone={c.visibility === 'private' ? 'muted' : 'primary'}>{VIS_LABEL[c.visibility]}</Pill>
                {!owner && c.ownerName && <span>by {c.ownerName}</span>}
              </div>
            </>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {owner ? (
            <>
              {!editing && <Button variant="secondary" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" />Edit</Button>}
              <Button onClick={() => setShareOpen(!shareOpen)} aria-expanded={shareOpen}><Share2 className="h-3.5 w-3.5" />Share</Button>
            </>
          ) : (
            <Button onClick={() => run(async () => {
              const r = await api<{ id: string; added: number; skipped: number }>(`/api/collections/${c.id}/copy`, { method: 'POST', json: token ? { token } : {} });
              toast.success(`Saved a copy with ${plural(r.added, 'reel')}`, r.skipped ? { description: `${plural(r.skipped, 'unavailable reel')} ${r.skipped === 1 ? 'was' : 'were'} skipped.` } : undefined);
              navigate(`/collections/${r.id}`);
            })}><Copy className="h-3.5 w-3.5" />Save a copy</Button>
          )}
        </div>
      </section>

      {owner && shareOpen && <SharePanel c={c} onChange={setC} />}

      <section className="rounded-3xl border border-border bg-card p-3 shadow-xs sm:p-4">
        {!c.items.length && <p className="p-6 text-center text-sm text-muted-foreground">No reels yet. Use <b>Add to collection</b> on any reel in a creator report, or from the extension's side panel on Instagram.</p>}
        <ol className="divide-y divide-border">
          {c.items.map((item, i) => (
            <li key={item.reelPk} draggable={owner} onDragStart={() => setDragging(item.reelPk)} onDragEnd={() => setDragging(null)}
              onDragOver={(e) => owner && e.preventDefault()} onDrop={() => { if (dragging) move(dragging, i); setDragging(null); }}
              className={cn('grid grid-cols-[auto_1fr_auto] items-center gap-3 py-2.5 md:grid-cols-[auto_1fr_minmax(160px,280px)_auto]', dragging === item.reelPk && 'opacity-40', owner && 'cursor-grab')}>
              {owner ? (
                <span className="flex flex-col">
                  <button aria-label="Move up" disabled={i === 0} onClick={() => move(item.reelPk, i - 1)} className="rounded p-0.5 text-muted-foreground hover:bg-secondary disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button aria-label="Move down" disabled={i === c.items.length - 1} onClick={() => move(item.reelPk, i + 1)} className="rounded p-0.5 text-muted-foreground hover:bg-secondary disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                </span>
              ) : <span className="w-6 text-center text-xs font-bold text-muted-foreground">{i + 1}</span>}
              {item.reel ? <ItemRow reel={item.reel} tiers={settings?.tiers ?? null} onOpen={() => setOpen(item.reelPk)} /> : <span className="text-sm italic text-muted-foreground">This reel is no longer available.</span>}
              <div className="col-span-3 md:col-span-1"><Note owner={owner} note={item.note} onSave={(note) => run(() => api(`/api/collections/${c.id}/items/${item.reelPk}`, { method: 'PATCH', json: { note } }))} /></div>
              {owner && <button aria-label="Remove from collection" title="Remove from collection" onClick={() => run(() => api(`/api/collections/${c.id}/items/${item.reelPk}`, { method: 'DELETE' }))} className="row-start-1 rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-rose-500 md:row-start-auto"><X className="h-4 w-4" /></button>}
            </li>
          ))}
        </ol>
      </section>

      {owner && <Button variant="outline-danger" onClick={() => setConfirmDelete(true)}><Trash2 className="h-3.5 w-3.5" />Delete collection</Button>}
      <ConfirmDialog open={confirmDelete} title={`Delete "${c.name}"?`} danger confirmLabel="Delete" body={<p>This removes the collection and its notes for you and everyone you shared it with. The reels themselves stay.</p>}
        onCancel={() => setConfirmDelete(false)} onConfirm={() => run(async () => { await api(`/api/collections/${c.id}`, { method: 'DELETE' }); navigate('/collections'); })} />

      {openIdx >= 0 && settings && (
        <ReelInspector reels={openable} index={openIdx} onIndex={(i) => setOpen(openable[i]!.pk)} onClose={() => setOpen(null)}
          handle={openable[openIdx]!.ownerHandle ?? ''} tiers={settings.tiers} secondsPerCredit={settings.secondsPerCredit} retentionDays={settings.videoRetentionDays} saveVideos={settings.saveVideos}
          bookmarked={marks ?? new Set()} onTranscribed={() => void load()}
          onBookmark={(pk, on) => setMarks((m) => { const n = new Set(m); if (on) n.add(pk); else n.delete(pk); return n; })} />
      )}
    </div>
  );
}

function ItemRow({ reel, tiers, onOpen }: { reel: ItemReel; tiers: { legendary: number; viral: number; outlier: number; above: number } | null; onOpen: () => void }) {
  return (
    <button className="grid min-w-0 grid-cols-[44px_1fr_auto] items-center gap-3 text-left" onClick={onOpen}>
      {reel.thumbUrl ? <img className="h-[78px] w-11 rounded-md object-cover" src={reel.thumbUrl} alt="" referrerPolicy="no-referrer" loading="lazy" /> : <span className="h-[78px] w-11 rounded-md bg-secondary" />}
      <span className="min-w-0">
        <span className="block truncate text-sm"><b>@{reel.ownerHandle}</b> {reel.caption?.split('\n')[0] ?? ''}</span>
        <span className="block text-xs text-muted-foreground">{date(reel.postedAt)} · {duration(reel.durationS)} · {compact(reel.views)} views{reel.hasTranscript ? ' · T' : ''}</span>
        {reel.hook && <span className="mt-0.5 block truncate text-xs italic text-amber-700 dark:text-amber-300">“{reel.hook}”</span>}
      </span>
      {tiers && reel.outlierScore !== null && reel.outlierScore >= tiers.above ? <TierBadge score={reel.outlierScore} tiers={tiers} /> : reel.outlierScore !== null ? <span className="text-xs font-bold tabular-nums text-muted-foreground">{formatWiseScore(reel.outlierScore)}</span> : null}
    </button>
  );
}

function Note({ owner, note, onSave }: { owner: boolean; note: string | null; onSave: (n: string) => void }) {
  const [edit, setEdit] = useState(false);
  if (!owner) return note ? <p className="rounded-lg bg-primary/5 px-2.5 py-1.5 text-xs">{note}</p> : null;
  if (!edit) return <button onClick={() => setEdit(true)} className="w-full rounded-lg px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-secondary">{note ? <span className="text-foreground">{note}</span> : '+ note'}</button>;
  return (
    <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); onSave(String(new FormData(e.currentTarget).get('note') ?? '')); setEdit(false); }}>
      <input name="note" defaultValue={note ?? ''} maxLength={1000} aria-label="Note" placeholder="Why you saved this" autoFocus className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs outline-none focus:border-primary" />
      <Button type="submit" size="sm">Save</Button>
    </form>
  );
}

function SharePanel({ c, onChange }: { c: CollectionView; onChange: (c: CollectionView) => void }) {
  const [email, setEmail] = useState('');
  const [rotate, setRotate] = useState(false);
  const allowed = new Set<Visibility>(c.limits?.shareLevels ?? ['private']);
  const call = async (fn: () => Promise<CollectionView>) => {
    try { onChange(await fn()); } catch (e) { toast.error((e as Error).message); }
  };
  const levels: Visibility[] = ['private', 'people', 'signed_in_link', 'anyone_link'];
  return (
    <section className="space-y-5 rounded-3xl border border-border bg-card p-6 shadow-xs">
      <h2 className="font-extrabold">Share</h2>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Who can see it</legend>
        {levels.map((v) => {
          const needsLink = v === 'signed_in_link' || v === 'anyone_link';
          const disabled = !allowed.has(v) || (needsLink && !c.linkAvailable);
          return (
            <label key={v} className={cn('flex cursor-pointer items-start gap-3 rounded-xl border p-3', c.visibility === v ? 'border-primary bg-primary/5' : 'border-border', disabled && 'cursor-not-allowed opacity-50')}>
              <input type="radio" name="vis" className="mt-0.5" checked={c.visibility === v} disabled={disabled}
                onChange={() => call(() => api<CollectionView>(`/api/collections/${c.id}`, { method: 'PATCH', json: { visibility: v } }))} />
              <span className="text-sm"><b>{VIS_LABEL[v]}</b><span className="block text-xs text-muted-foreground">{VIS_HELP[v]}{!allowed.has(v) ? ' Upgrade to use this.' : ''}</span></span>
            </label>
          );
        })}
        {c.visibility === 'public' && <p className="text-xs font-semibold text-emerald-600">Published in Explore.</p>}
      </fieldset>

      {c.link && (
        <div className="flex flex-wrap gap-2">
          <Input icon={<Link2 className="h-4 w-4" />} className="min-w-[240px] flex-1" readOnly value={c.link} aria-label="Share link" onFocus={(e) => e.currentTarget.select()} />
          <Button onClick={() => void copyText(c.link!, { success: 'Link copied' })}><Copy className="h-3.5 w-3.5" />Copy</Button>
          <Button variant="secondary" onClick={() => setRotate(true)}>New link</Button>
          <Button variant="secondary" onClick={() => call(() => api(`/api/collections/${c.id}/link`, { method: 'POST', json: { action: 'revoke' } }))}>Turn off</Button>
        </div>
      )}

      {allowed.has('people') && (
        <div className="space-y-2">
          <form className="flex max-w-md gap-2" onSubmit={(e) => { e.preventDefault(); void call(() => api(`/api/collections/${c.id}/access`, { method: 'POST', json: { email } })).then(() => setEmail('')); }}>
            <Input type="email" className="flex-1" placeholder="name@gmail.com" aria-label="Invite by email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button type="submit" variant="secondary" disabled={!email}>Invite</Button>
          </form>
          {!!c.invites?.length && (
            <ul className="space-y-1">
              {c.invites.map((i) => (
                <li key={i.email} className="flex items-center gap-2 text-sm">{i.email}<span className="text-xs text-muted-foreground">{i.joined ? 'has opened it' : 'invited'}</span>
                  <button aria-label={`Remove ${i.email}`} onClick={() => call(() => api(`/api/collections/${c.id}/access/${encodeURIComponent(i.email)}`, { method: 'DELETE' }))} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-rose-500"><X className="h-3.5 w-3.5" /></button></li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">They'll find it under "Shared with me" after signing in with that Google account. We email them when you add them.</p>
        </div>
      )}

      <ConfirmDialog open={rotate} title="Make a new link?" body={<p>The old link stops working straight away. Anyone who should keep access needs the new one.</p>} confirmLabel="Make a new link"
        onCancel={() => setRotate(false)} onConfirm={() => { setRotate(false); void call(() => api(`/api/collections/${c.id}/link`, { method: 'POST', json: { action: 'rotate' } })); }} />
      {c.visibility !== 'public' && (
        c.publishRequest === 'pending'
          ? <p className="text-xs text-muted-foreground">Publish request sent; the team will review it.</p>
          : <Button variant="ghost" size="sm" onClick={() => call(async () => { await api(`/api/collections/${c.id}/publish-request`, { method: 'POST' }); return { ...c, publishRequest: 'pending' }; })}>Request to publish in Explore</Button>
      )}
    </section>
  );
}
